import { Injectable } from '@angular/core';
import { ScannedBy, StoredTicket } from '@seatix/check-in-domain';
import Dexie, { Table } from 'dexie';

export interface StoredCategoryGate {
  id?: number;
  gate_id?: number;
  name?: string;
  restriction_type?: 'soft' | 'hard';
}

export interface StoredSeatCategory {
  event_id: number;
  id: number;
  name: string;
  color: string;
  gates: StoredCategoryGate[];
}

export type QueuedScanData = Pick<
  StoredTicket,
  | 'event_id'
  | 'ticket_id'
  | 'signature'
  | 'status'
  | 'scan_id'
  | 'scanned_at'
  | 'scanned_by'
>;

export interface SyncMetadata {
  synced: boolean;
  sync_attempts: number;
  last_attempt_at?: number;
  error?: string;
}

export interface SyncQueueItem extends QueuedScanData, SyncMetadata {
  id?: number;
}

@Injectable({
  providedIn: 'root',
})
export class IndexedDBService extends Dexie {
  seatCategories!: Table<StoredSeatCategory, [number, number]>;
  ticketInventory!: Table<StoredTicket, number>;
  syncQueue!: Table<SyncQueueItem, number>;

  constructor() {
    super('SeatixScannerDB');

    this.version(2).stores({
      tickets: 'id, event_id, status, seat_category_id',

      gateRestrictions: '[gate_id+seat_category_id], gate_id, seat_category_id',

      eventMetadata: 'event_id',

      recentScans: 'scan_id, [event_id+ticket_id], timestamp',

      syncQueue: '++id, timestamp, synced',

      cachedTickets: 'ticketId, eventId, lastSync',
    });

    this.version(3).stores({
      seatCategories: '[event_id+id], event_id, id',
    });

    this.version(4).stores({
      ticketInventory: 'ticket_id, event_id, status',
    });

    this.version(5).stores({
      ticketInventory: 'ticket_id, event_id, status, [event_id+scanned_at]',
    });

    this.version(6).stores({
      recentScans: null,
    });

    this.version(7).stores({
      tickets: null,
      eventMetadata: null,
      syncQueue: null,
      cachedTickets: null,
    });

    this.version(8).stores({
      gateRestrictions: null,
    });

    this.version(9).stores({
      ticketInventory:
        'ticket_id, event_id, status, signature, [event_id+scanned_at]',
    });

    this.version(10).stores({
      syncQueue: '++id, event_id, synced, scanned_at',
    });
  }

  async clearAllData(): Promise<void> {
    await this.seatCategories.clear();
    await this.ticketInventory.clear();
    await this.syncQueue.clear();
  }
}
